/**
 * Aspect — NetworkManager (client side of the Nervous System)
 * --------------------------------------------------------------
 * Owns the single WebSocket to the bridge. Responsibilities:
 *   - declare role "viewport" via the `hello` handshake on every (re)connect
 *   - parse inbound JSON-RPC 2.0 frames and re-emit them as PlayCanvas app
 *     events (`net:<method>`) so the rest of the client stays DOM/socket-agnostic
 *   - reconnect with exponential backoff + jitter, capped
 *   - queue outbound frames while disconnected and flush them once ready
 *
 * This module never touches the DOM (per engine rules). It fires `net:status`
 * app events; a thin binder in the app shell reflects those into the UI.
 *
 * Heartbeat note: the bridge sends WebSocket-level PING control frames. Browsers
 * auto-respond with PONG, so liveness is handled by the platform — no app code
 * needed here.
 */

import { bridgeUrl } from "./endpoints.js";

const STATE = Object.freeze({
  CONNECTING: "connecting",
  READY: "ready",
  RECONNECTING: "reconnecting",
  CLOSED: "closed",
});

export class NetworkManager {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {object} [opts]
   * @param {string} [opts.url]
   * @param {string} [opts.role]
   */
  constructor(app, opts = {}) {
    this.app = app;
    this.url = opts.url ?? bridgeUrl();
    this.role = opts.role ?? "viewport";

    this.ws = null;
    this.state = STATE.CLOSED;
    this.shouldRun = false;

    // Reconnect backoff config.
    this.baseDelay = 500;
    this.maxDelay = 10000;
    this.attempt = 0;
    this.reconnectTimer = null;

    // Frames queued while the socket is not READY (flushed on handshake ack).
    this.outbound = [];

    // Pending JSON-RPC requests awaiting a result/error, keyed by id.
    this.pending = new Map();
    this.nextId = 1;
  }

  connect() {
    this.shouldRun = true;
    this._open();
  }

  _setState(state, meta = {}) {
    this.state = state;
    this.app.fire("net:status", { state, ...meta });
  }

  _open() {
    this._setState(this.attempt > 0 ? STATE.RECONNECTING : STATE.CONNECTING, { attempt: this.attempt });

    let ws;
    try {
      ws = new WebSocket(this.url);
    } catch (err) {
      console.error("[net] WebSocket construct failed", err);
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.addEventListener("open", () => {
      // Declare our role first; routing only begins after the bridge acks.
      this._raw({ jsonrpc: "2.0", method: "hello", params: { role: this.role } });
    });

    ws.addEventListener("message", (ev) => this._onMessage(ev.data));

    ws.addEventListener("close", (ev) => {
      this._setState(STATE.CLOSED, { code: ev.code });
      this.app.fire("net:closed", { code: ev.code, reason: ev.reason });
      if (this.shouldRun) this._scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      // The close handler drives reconnect; just surface it.
      this.app.fire("net:error", {});
    });
  }

  _onMessage(data) {
    let msg;
    try {
      msg = JSON.parse(typeof data === "string" ? data : data.toString());
    } catch {
      console.warn("[net] dropped non-JSON frame");
      return;
    }

    // Handshake acknowledgement: connection is now routable -> flush queue.
    if (msg.method === "connection:ready") {
      this.attempt = 0;
      this._setState(STATE.READY, { clientId: msg.params?.clientId });
      this.app.fire("net:ready", msg.params ?? {});
      this._flush();
      return;
    }

    // Response to one of our requests.
    if (msg.id !== undefined && msg.id !== null && (msg.result !== undefined || msg.error !== undefined)) {
      const entry = this.pending.get(msg.id);
      if (entry) {
        this.pending.delete(msg.id);
        if (msg.error) entry.reject(msg.error);
        else entry.resolve(msg.result);
      }
      return;
    }

    // Server-initiated command/notification -> broadcast as an app event.
    if (typeof msg.method === "string") {
      this.app.fire(`net:${msg.method}`, msg.params ?? {}, msg.id ?? null);
    }
  }

  /** Low-level send that bypasses the queue (used for the handshake itself). */
  _raw(frame) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
      return true;
    }
    return false;
  }

  /** Send a fire-and-forget notification (queued if not ready). */
  notify(method, params) {
    const frame = { jsonrpc: "2.0", method, ...(params === undefined ? {} : { params }) };
    if (this.state === STATE.READY && this._raw(frame)) return;
    this.outbound.push(frame);
  }

  /** Send a request and await its result. Rejects on error or disconnect. */
  request(method, params, timeoutMs = 10000) {
    const id = this.nextId++;
    const frame = { jsonrpc: "2.0", id, method, ...(params === undefined ? {} : { params }) };
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`request '${method}' timed out`));
      }, timeoutMs);
      this.pending.set(id, {
        resolve: (v) => {
          clearTimeout(timer);
          resolve(v);
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
      });
      if (this.state === STATE.READY && this._raw(frame)) return;
      this.outbound.push(frame);
    });
  }

  _flush() {
    if (!this.outbound.length) return;
    const queued = this.outbound;
    this.outbound = [];
    for (const frame of queued) {
      if (!this._raw(frame)) {
        // Socket dropped mid-flush; requeue the remainder and bail.
        this.outbound.push(frame);
      }
    }
  }

  _scheduleReconnect() {
    if (!this.shouldRun || this.reconnectTimer) return;
    const expo = Math.min(this.maxDelay, this.baseDelay * 2 ** this.attempt);
    // Full jitter avoids thundering-herd reconnect storms.
    const delay = Math.random() * expo;
    this.attempt += 1;
    this._setState(STATE.RECONNECTING, { attempt: this.attempt, delayMs: Math.round(delay) });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._open();
    }, delay);
  }

  destroy() {
    this.shouldRun = false;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    for (const { reject } of this.pending.values()) reject(new Error("network destroyed"));
    this.pending.clear();
    try {
      this.ws?.close(1000, "client teardown");
    } catch {
      /* ignore */
    }
    this.ws = null;
  }
}

export { STATE as NET_STATE };
